import { createSelector } from '@reduxjs/toolkit'
import { TRootState } from 'store/types'
import { IAIImageModel } from 'store/types/aiImage'
import { creditsSelectors } from 'store/features/credits/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { MediaStatus } from 'types/media'
import { AIVideoSocketMessageStatus } from 'types/aiVideo'
import { getCreditCostPerModel } from 'store/features/aiImage/utils'

const selector = (state: TRootState) => state.aiImage

// Generating selectors
const isRequestingGeneration = createSelector(
  selector,
  state => state.isRequestingGeneration
)

// Active tab selector
const activeTab = createSelector(selector, state => state.activeTab)

// Ref images uploading selectors
const isRefImagesUploading = createSelector(
  selector,
  state => state.isRefImagesUploading
)

// Models selectors
const models = createSelector(selector, state => state.models)
const isModelsLoading = createSelector(
  selector,
  state => state.models.isLoading
)
const modelsData = createSelector(models, state => state.data || [])

// Styles selectors
const styles = createSelector(selector, state => state.styles)
const isStylesLoading = createSelector(
  selector,
  state => state.styles.isLoading
)
const stylesData = createSelector(styles, state => state.data || [])

// Restyling styles selectors
const restylingStyles = createSelector(selector, state => state.restylingStyles)
const isRestylingStylesLoading = createSelector(
  selector,
  state => state.restylingStyles.isLoading
)
const restylingStylesData = createSelector(
  restylingStyles,
  state => state.data || []
)

// History selectors
const history = createSelector(selector, state => state.history)
const historyData = createSelector(history, state => state.data || [])
const historyDataLength = createSelector(historyData, data => data?.length || 0)
const isHistoryLoading = createSelector(history, state => state.isLoading)
const totalHistoryCount = createSelector(history, state => state.totalCount)
const isHistoryScopedToCurrentProject = createSelector(
  history,
  state => state.isScopedToCurrentProject
)

// Delete selectors
const deleteItemIds = createSelector(selector, state => state.deleteItemIds)
const isCurrentItemDeleting = createSelector(
  [deleteItemIds, (_, itemId: number) => itemId],
  (deleteItemIds, itemId) =>
    (itemId && deleteItemIds?.includes(itemId)) || false
)

// Generate form selectors
const generateForm = createSelector(selector, state => state.generateForm)
const prompt = createSelector(generateForm, form => form.prompt)
const aspectRatio = createSelector(generateForm, form => form.aspectRatio)
const styleId = createSelector(generateForm, form => form.styleId)
const refImages = createSelector(generateForm, form => form.refImages)
const modelCode = createSelector(generateForm, form => form.modelCode)
const sourceModelCode = createSelector(
  generateForm,
  form => form.sourceModelCode
)
const aiImagePath = createSelector(generateForm, form => form.aiImagePath)
const targetMediaItemId = createSelector(
  generateForm,
  form => form.targetMediaItemId
)

// Computed selectors
const hasModels = createSelector(modelsData, data => data.length > 0)
const hasStyles = createSelector(stylesData, data => data.length > 0)
const hasHistory = createSelector(historyData, data => data.length > 0)

// Current model properties

const modelMaxRefImagesCount = createSelector(
  modelCode,
  modelsData,
  (modelCode, modelsData) =>
    modelsData.find((model: IAIImageModel) => model.code === modelCode)
      ?.referenceImagesCount || 0
)

const referenceImagesCount = createSelector(
  refImages,
  refImages => refImages?.length || 0
)
const availableRefImgsCount = createSelector(
  referenceImagesCount,
  modelMaxRefImagesCount,
  (referenceImagesCount, maxRefImagesCount) =>
    maxRefImagesCount - referenceImagesCount
)

const aspectRatios = createSelector(
  modelCode,
  modelsData,
  (modelCode, modelsData) =>
    modelsData.find((model: IAIImageModel) => model.code === modelCode)
      ?.possibleAspectRatios || []
)

const selectedModel = createSelector(
  modelCode,
  modelsData,
  (modelCode, modelsData) =>
    modelsData.find((model: IAIImageModel) => model.code === modelCode) || null
)

const isSelectedModelUnlimited = createSelector(
  selectedModel,
  userSelectors.unlimitedAiImageModelIds,
  (model, unlimitedIds) => {
    if (!model || !unlimitedIds?.length) {
      return false
    }
    return unlimitedIds.includes(model.id)
  }
)

const generationCost = createSelector(
  modelCode,
  modelsData,
  isSelectedModelUnlimited,
  (modelCode, modelsData, isUnlimited) => {
    if (isUnlimited) {
      return 0
    }
    return getCreditCostPerModel({ modelCode, modelsData })
  }
)

const hasEnoughCredits = createSelector(
  creditsSelectors.userCredits,
  generationCost,
  (userCredits, generationCost) => userCredits && userCredits >= generationCost
)

const aiImageOverlayData = createSelector(
  [generateForm, projectDataSelectors.mediaItems],
  (generationParams, mediaItems) => {
    const statusMap = {
      [MediaStatus.PENDING]: AIVideoSocketMessageStatus.PROCESSING,
      [MediaStatus.OK]: AIVideoSocketMessageStatus.COMPLETED,
      [MediaStatus.ERROR]: AIVideoSocketMessageStatus.FAILED
    }

    const mediaItem = mediaItems.find(
      item => item.id === String(generationParams.targetMediaItemId)
    )

    return {
      ...generationParams,
      aiImagePath: mediaItem?.meta?.path,
      status: statusMap[mediaItem?.meta?.status as MediaStatus]
    }
  }
)

const targetMediaItem = createSelector(
  [projectDataSelectors.mediaItems, targetMediaItemId],
  (mediaItems, targetMediaItemId) =>
    mediaItems.find(item => item.id === String(targetMediaItemId))
)

const defaultModel = createSelector(modelsData, modelsData => {
  const defaultModel = modelsData.find((model: IAIImageModel) =>
    model.isDefaultFor.includes('v-editor')
  )!
  return defaultModel
})

const defaultModelCost = createSelector(
  [defaultModel, modelsData],
  (defaultModel, modelsData) => {
    return getCreditCostPerModel({
      modelCode: defaultModel?.code,
      modelsData
    })
  }
)

const refImageStyleCreditsCost = createSelector(
  [modelsData, defaultModelCost],
  (modelsData: IAIImageModel[], defaultModelCost: number) => {
    const refImageModel = modelsData.find((model: IAIImageModel) =>
      model.isDefaultFor.includes('restyled-image-to-video')
    )
    return refImageModel?.cost || defaultModelCost
  }
)

const refImageSizeCreditsCost = createSelector(
  [modelsData, defaultModelCost],
  (modelsData: IAIImageModel[], defaultModelCost: number) => {
    const resizeModel = modelsData.find((model: IAIImageModel) =>
      model.isDefaultFor.includes('resized-image-to-video')
    )
    return resizeModel?.cost || defaultModelCost
  }
)

const versionHistoryItems = createSelector(
  selector,
  state => state.versionHistory.items
)

const isVersionHistoryLoading = createSelector(
  selector,
  state => state.versionHistory.isLoading
)

const isVersionHistoryLoaded = createSelector(
  selector,
  state => state.versionHistory.isLoaded
)

const showVersionHistoryTab = createSelector(
  selector,
  state => state.showVersionHistoryTab
)

export const aiImageSelectors = {
  activeTab,
  isRequestingGeneration,
  models,
  isModelsLoading,
  modelsData,
  hasModels,
  styles,
  isStylesLoading,
  stylesData,
  hasStyles,
  history,
  historyData,
  historyDataLength,
  isHistoryLoading,
  totalHistoryCount,
  isHistoryScopedToCurrentProject,
  hasHistory,
  deleteItemIds,
  isCurrentItemDeleting,
  modelMaxRefImagesCount,
  referenceImagesCount,
  availableRefImgsCount,
  aspectRatios,
  generationCost,
  hasEnoughCredits,
  isRefImagesUploading,
  defaultModelCost,
  defaultModel,
  restylingStyles,
  isRestylingStylesLoading,
  restylingStylesData,
  // Generate form selectors
  generateForm,
  prompt,
  aspectRatio,
  styleId,
  refImages,
  modelCode,
  sourceModelCode,
  aiImagePath,
  targetMediaItemId,
  targetMediaItem,
  aiImageOverlayData,
  refImageStyleCreditsCost,
  refImageSizeCreditsCost,
  versionHistoryItems,
  isVersionHistoryLoading,
  isVersionHistoryLoaded,
  showVersionHistoryTab
}
